import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronsRight } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import GoBackButton from "@/common/components/GoBackButton";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useQueryClient } from "@tanstack/react-query";
import Checkbox from "@/common/components/Checkbox";
import useTrackerIntegration from "../../../hooks/trackerIntegrations/useTrackerIntegration";
import { createSchema, TrackerIntegrationValidationSchema } from "../../../schemas/trackerIntegrations/create.schema";
import useUpdateTrackerIntegration from "../../../hooks/trackerIntegrations/useUpdateTrackerIntegration";
import TextField from "@/common/components/TextField";
import useCompany from "@/common/hooks/queries/useCompany";

const Update = () => {
  const { companyId, id } = useParams();

  const goBack = useGoBack();
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const queryClient = useQueryClient();

  const {
    data: initialTrackerIntegration,
    isLoading
  } = useTrackerIntegration(id || '');

  const { data: company } = useCompany({ id: companyId || '' })

  const {
    mutate,
    isPending
  } = useUpdateTrackerIntegration({
    companyId: companyId || '',
    id: id || '',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-integrations', id] });
      goBack({ fallback: `/tracker-integrations/${id}` })
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    }
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<TrackerIntegrationValidationSchema>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      active: initialTrackerIntegration?.active,
      option_id: {
        value: initialTrackerIntegration?.option?.id
      },
      parameters: initialTrackerIntegration?.parameters
    }
  });

  const onSubmit: SubmitHandler<TrackerIntegrationValidationSchema> = (data) => mutate(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback={`/tracker-integrations/${id}`} />

          <p className="text-white-400 font-normal">
            {company?.general_information?.fantasy_name}
          </p>
          <ChevronsRight color='#ffffffb3' />

          <h2>
            Editar Integração de rastreamento
          </h2>
        </div>

        <div className="flex gap-x-4 items-center">
          <Link to={`/companies/${companyId}/tracker-integrations/${id}`}>
            <Button variant="outline-error">Cancelar</Button>
          </Link>
          <Button data-testid='update_tracker_integration' type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" isLoading={isPending}>
            Confirmar
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Box>Carregando...</Box>
      ) : (
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <p className="text-lg font-bold">
          Editar integração de rastreamento
        </p>

          <TextField
            label="Tipo de integração"
            value={initialTrackerIntegration?.option.provider_klass}
            data-testid="option_id_selector"
            disabled
          />

          {initialTrackerIntegration?.parameters && (
            <div className="grid grid-cols-2 gap-y-4 gap-x-2">
              {Object.keys(initialTrackerIntegration?.parameters)
                .sort((a, b) => a.localeCompare(b))
                .map((param) => (
                  <TextField
                    label={param}
                    {...register(`parameters.${param}`)}
                    errorMessage={errors?.parameters?.[param]?.message}
                    defaultValue={initialTrackerIntegration?.parameters[param]}
                    markAsRequired
                  />
                ))}
            </div>
          )}

          <Checkbox
            label="Habilitada"
            defaultChecked={initialTrackerIntegration?.active}
            {...register('active')}
          />
        </Box>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Update;
